import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@material-ui/core";
import { Description, Folder, Image, Search } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const FileLibraryDialog = ({ open, onClose, onSelect }) => {
  const [libraries, setLibraries] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/files/list");
        setLibraries(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const entries = useMemo(() => {
    const term = search.trim().toLowerCase();
    return libraries.flatMap(library => (library.options || [])
      .filter(option => option.path)
      .map(option => ({
        ...option,
        libraryId: library.id,
        libraryName: library.name,
        fileName: option.path.split("/").pop(),
        defaultMessage: option.name || library.message || ""
      })))
      .filter(entry => !term || `${entry.fileName} ${entry.name} ${entry.libraryName}`.toLowerCase().includes(term));
  }, [libraries, search]);

  const selectFile = async entry => {
    setDownloading(entry.id);
    try {
      const response = await api.get(`/files/${entry.libraryId}/options/${entry.id}/download`, { responseType: "blob" });
      const mimeType = response.headers["content-type"] || entry.mediaType || "application/octet-stream";
      const file = new File([response.data], entry.fileName || "arquivo", { type: mimeType });
      onSelect({ file, caption: entry.defaultMessage });
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setDownloading(null);
    }
  };

  const iconFor = entry => entry.mediaType && entry.mediaType.startsWith("image/") ? <Image /> : <Description />;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Biblioteca de arquivos</DialogTitle>
      <DialogContent dividers>
        <Typography color="textSecondary" paragraph>
          Selecione um arquivo já carregado pela empresa para anexá-lo à conversa.
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Buscar arquivo ou lista"
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
        ) : entries.length ? (
          <List>
            {entries.map((entry, index) => (
              <React.Fragment key={`${entry.libraryId}-${entry.id}`}>
                {index > 0 && <Divider />}
                <ListItem button onClick={() => selectFile(entry)} disabled={downloading !== null}>
                  <ListItemIcon>{downloading === entry.id ? <CircularProgress size={22} /> : iconFor(entry)}</ListItemIcon>
                  <ListItemText
                    primary={entry.fileName || "Arquivo"}
                    secondary={`${entry.libraryName}${entry.defaultMessage ? ` • ${entry.defaultMessage}` : ""}`}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box textAlign="center" py={5}>
            <Folder color="disabled" style={{ fontSize: 48 }} />
            <Typography color="textSecondary">Nenhum arquivo carregado foi encontrado.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

export default FileLibraryDialog;
